import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { UsersRound } from "lucide-react";

import ViewContainer from "../../../../../components/layout/view/ViewContainer";

import ContentPanel from "../../../../../components/layout/content/ContentPanel";
import ContentPanelHeader from "../../../../../components/layout/content/ContentPanelHeader";

import ContentGrid from "../../../../../components/grids/content/ContentGrid";
import EmptyState from "../../../../../components/feedback/EmptyState";

import { useCreatorGroups } from "../../hooks/useCreatorGroups";

import GroupCard from "../../../components/cards/GroupCard";
import GroupCreatorDashboard from "../groupDashboard/GroupCreatorDashboard";

import "./GroupsCreatorView.css";

const GRID_CONFIG = {
  columns: 4,
  tabletColumns: 3,
  mobileColumns: 2,
  gap: 16,
  showEmpty: false,
};

const getErrorMessage = (error) =>
  typeof error === "string"
    ? error
    : error?.response?.data?.message ||
      error?.message ||
      "Unable to manage groups.";

export default function GroupsCreatorView({ username, refreshKey = 0 }) {
  const [params, setParams] = useSearchParams();
  const selectedTag = params.get("g") || "";

  const { getCreatorGroups, deleteCreatorGroup } = useCreatorGroups(username);

  const fetchRef = useRef(getCreatorGroups);
  fetchRef.current = getCreatorGroups;

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingTag, setDeletingTag] = useState("");

  useEffect(() => {
    let active = true;

    setGroups([]);
    setLoading(true);
    setError("");

    if (!username) {
      setLoading(false);
      return;
    }

    const loadGroups = async () => {
      try {
        const data = await fetchRef.current();

        if (active) {
          setGroups(Array.isArray(data) ? data : []);
        }
      } catch (requestError) {
        if (active) {
          setError(getErrorMessage(requestError));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void loadGroups();

    return () => {
      active = false;
    };
  }, [username, refreshKey]);

  const updateSelectedTag = (tag) => {
    setParams((current) => {
      const next = new URLSearchParams(current);

      if (tag) {
        next.set("g", String(tag));
      } else {
        next.delete("g");
      }

      return next;
    });
  };

  const handleDelete = async (group) => {
    const tag = String(group?.groupTag || "");

    if (!tag || deletingTag) return;

    if (!window.confirm(`Delete "${group.groupName}"?`)) {
      return;
    }

    setDeletingTag(tag);
    setError("");

    try {
      const result = await deleteCreatorGroup(tag);

      if (result?.success === false) {
        throw new Error(result.message || "Unable to delete group.");
      }

      setGroups((current) =>
        current.filter((item) => String(item.groupTag) !== tag),
      );

      if (selectedTag === tag) {
        updateSelectedTag("");
      }
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setDeletingTag("");
    }
  };

  const renderGroup = (group) => (
    <GroupCard
      key={group.groupTag}
      group={group}
      onOpen={() => updateSelectedTag(group.groupTag)}
    />
  );

  if (selectedTag) {
    return (
      <GroupCreatorDashboard
        key={`${username}-${selectedTag}`}
        username={username}
        groupTag={selectedTag}
        onBack={() => updateSelectedTag("")}
        onDeleteGroup={handleDelete}
      />
    );
  }

  return (
    <ViewContainer>
      <ContentPanel>
        <ContentPanelHeader
          title="Groups"
          subTitle="Manage the groups belonging to this creator profile."
        />

        {error && (
          <p className="creator-groups__error" role="alert">
            {error}
          </p>
        )}

        {loading && groups.length === 0 ? (
          <ContentGrid
            {...GRID_CONFIG}
            items={[]}
            loading
            skeletonCount={8}
            renderItem={() => null}
          />
        ) : groups.length === 0 && !error ? (
          <EmptyState
            icon={<UsersRound size={28} />}
            title="No groups yet"
            description="Create a group to get started."
          />
        ) : (
          <ContentGrid
            {...GRID_CONFIG}
            items={groups}
            renderItem={renderGroup}
          />
        )}
      </ContentPanel>
    </ViewContainer>
  );
}
